import { useCallback, useEffect, useState } from "react";
import { Scale, X, CheckCircle2, UploadCloud, FileText, ShieldCheck } from "lucide-react";
import api, { inr, fmtDate, fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STATUS_CLS = {
  WEIGHT_DISCREPANCY_DETECTED: "bg-slate-100 text-slate-500",
  CUSTOMER_REVIEW: "bg-amber-50 text-amber-700",
  CUSTOMER_DISPUTED: "bg-blue-50 text-blue-700",
  RESOLVED: "bg-emerald-50 text-emerald-600",
};
const statusLabel = (d) => {
  if (d.status === "RESOLVED") {
    if (d.resolution === "accepted") return "Resolved — Accepted";
    if (d.resolution === "approved") return "Resolved — Approved";
    if (d.resolution === "rejected") return "Resolved — Rejected";
  }
  return d.status.replace(/_/g, " ");
};

export default function Disputes() {
  const [disputes, setDisputes] = useState(null);
  const [target, setTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/disputes").then(({ data }) => setDisputes(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const accept = async (d) => {
    if (!window.confirm(`Accept courier weight for ${d.awb}? The deducted ${inr(d.deducted_amount)} adjustment will remain.`)) return;
    try {
      await api.post(`/disputes/${d.dispute_id}/accept`);
      toast.success("Courier weight accepted — dispute closed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const raise = async () => {
    if (reason.trim().length < 10) return toast.error("Please explain why the courier weight is incorrect (min 10 characters)");
    setBusy(true);
    try {
      await api.post(`/disputes/${target.dispute_id}/dispute`, { reason });
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await api.post(`/disputes/${target.dispute_id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Dispute raised — our team will verify your evidence");
      setTarget(null);
      setReason("");
      setFiles([]);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="disputes-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Weight Disputes</h1>
      <p className="text-sm text-slate-500">When a courier re-measures a shipment heavier than booked, only the difference amount is adjusted from your wallet. Accept the courier weight or raise a dispute with evidence.</p>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="disputes-table">
        <table className="w-full min-w-[1020px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">Dispute</th><th className="px-4 py-3.5">AWB</th>
              <th className="px-4 py-3.5">Booked</th><th className="px-4 py-3.5">Courier Chargeable</th>
              <th className="px-4 py-3.5">Original</th><th className="px-4 py-3.5">Revised</th>
              <th className="px-4 py-3.5">Difference</th><th className="px-4 py-3.5">Deducted</th>
              <th className="px-4 py-3.5">Status</th><th className="px-4 py-3.5">Date</th><th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {(disputes || []).map((d) => (
              <tr key={d.dispute_id} className="border-t border-slate-100" data-testid={`dispute-row-${d.awb}`}>
                <td className="mono px-5 py-3 font-semibold text-shipany-navy">{d.serial || "—"}</td>
                <td className="mono px-4 py-3 font-semibold text-shipany-blue">{d.awb}</td>
                <td className="mono px-4 py-3">{d.booked_weight} kg</td>
                <td className="mono px-4 py-3 font-semibold">{d.chargeable_weight} kg</td>
                <td className="mono px-4 py-3">{inr(d.original_charge)}</td>
                <td className="mono px-4 py-3">{inr(d.revised_charge)}</td>
                <td className="mono px-4 py-3 font-bold text-red-500">+{inr(d.difference_amount)}</td>
                <td className="mono px-4 py-3 font-semibold">{inr(d.deducted_amount)}{d.refund_amount ? <span className="block text-[10px] text-emerald-600">refunded {inr(d.refund_amount)}</span> : null}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${STATUS_CLS[d.status] || "bg-slate-100 text-slate-500"}`} data-testid={`dispute-status-${d.awb}`}>{statusLabel(d)}</span></td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(d.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setDetail(d)} data-testid={`dispute-view-${d.awb}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:border-shipany-blue hover:text-shipany-blue">View</button>
                    {d.status === "CUSTOMER_REVIEW" && (
                      <>
                        <button onClick={() => accept(d)} data-testid={`accept-btn-${d.awb}`} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Accept
                        </button>
                        <button onClick={() => { setTarget(d); setReason(""); setFiles([]); }} data-testid={`dispute-btn-${d.awb}`} className="flex items-center gap-1.5 rounded-lg bg-shipany-blue px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#0648B3]">
                          <Scale className="h-3.5 w-3.5" /> Raise Dispute
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {disputes && disputes.length === 0 && <tr><td colSpan={11} className="px-6 py-10 text-center text-slate-400">No weight discrepancies recorded.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="dispute-detail-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">{detail.serial} · AWB {detail.awb}</h2>
              <button onClick={() => setDetail(null)} data-testid="dispute-detail-close" className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50/40 p-4" data-testid="weight-discrepancy-model">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Dimension &amp; Weight</p>
              <div className="mono mt-2 space-y-1 text-xs text-slate-600">
                <p>{detail.booked_dimensions || "—"}</p>
                <p>Entered Weight: <span className="font-bold text-shipany-navy">{detail.booked_weight}Kg</span></p>
                <p>Volumetric Weight: <span className="font-bold text-shipany-navy">{detail.booked_volumetric ? `${detail.booked_volumetric}Kg` : "—"}</span></p>
                <p>Courier Weight: <span className="font-bold text-amber-700">{detail.chargeable_weight}Kg</span></p>
                <p>Courier Dimension: <span className="font-bold text-amber-700">{detail.courier_dimensions || "—"}</span></p>
              </div>
              <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-red-500">(Weight Discrepancy)</p>
              <p className="mono mt-1 text-[11px] text-slate-400">{detail.awb}{detail.order_number ? ` · ${detail.order_number}` : ""}</p>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-200 p-4" data-testid="detail-booked-card">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Your booking</p>
                <p className="mono mt-2 text-sm font-semibold">{detail.booked_weight} kg booked</p>
                {detail.booked_dimensions ? <p className="mono text-xs text-slate-500">{detail.booked_dimensions}</p> : null}
                <p className="mono mt-1 text-sm font-bold text-shipany-navy">{inr(detail.original_charge)}</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="detail-courier-card">
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Courier assessed{detail.courier_name ? ` · ${detail.courier_name}` : ""}</p>
                <p className="mono mt-2 text-sm font-semibold">{detail.chargeable_weight} kg chargeable{detail.courier_weight ? ` (${detail.courier_weight} kg dead)` : ""}</p>
                {detail.courier_dimensions ? <p className="mono text-xs text-slate-500">{detail.courier_dimensions}</p> : null}
                <p className="mono mt-1 text-sm font-bold text-amber-700">{inr(detail.revised_charge)}</p>
              </div>
            </div>
            <div className="mt-3 rounded-xl bg-slate-50 p-4 text-sm" data-testid="detail-adjustment">
              Difference <span className="mono font-bold text-red-500">+{inr(detail.difference_amount)}</span> — deducted from wallet <span className="mono font-bold">{inr(detail.deducted_amount)}</span>
              {detail.refund_amount ? <span className="text-emerald-600"> · refunded <span className="mono font-bold">{inr(detail.refund_amount)}</span></span> : null}
            </div>
            {detail.customer_reason ? <p className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700" data-testid="detail-customer-reason">Your claim: {detail.customer_reason}</p> : null}
            {detail.admin_remarks ? <p className="mt-2 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600" data-testid="detail-admin-remarks">ShipAny: {detail.admin_remarks}</p> : null}
            {(detail.evidence || []).length > 0 && (
              <div className="mt-4" data-testid="detail-evidence">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Evidence ({detail.evidence.length})</p>
                <div className="mt-2 space-y-1.5">
                  {detail.evidence.map((ev) => (
                    <a key={ev.file_id} href={`${API_BASE}/api/disputes/${detail.dispute_id}/evidence/${ev.file_id}`} target="_blank" rel="noreferrer"
                      data-testid={`evidence-${ev.file_id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-shipany-blue hover:border-shipany-blue">
                      <FileText className="h-3.5 w-3.5" /> {ev.filename} <span className="ml-auto text-slate-400">{Math.round(ev.size / 1024)} KB</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4" data-testid="detail-timeline">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Timeline</p>
              <div className="mt-2 space-y-1.5">
                {(detail.status_history || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
                    <ShieldCheck className="h-3 w-3 text-shipany-cyan" />
                    <span className="font-semibold text-slate-700">{h.status.replace(/_/g, " ")}</span>
                    {h.amount ? <span className="mono">{inr(h.amount)}</span> : null}
                    <span className="ml-auto">{fmtDateTime(h.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {target && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="dispute-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setTarget(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Dispute {target.awb}</h2>
              <button onClick={() => setTarget(null)} className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-2 text-sm text-slate-500">Booked {target.booked_weight} kg, courier charged {target.chargeable_weight} kg → {inr(target.difference_amount)} already deducted. Courier figures cannot be edited — tell us why they are wrong and attach proof.</p>
            <textarea data-testid="dispute-reason" rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Actual package weight is 5.2 KG on my weighing scale (photo attached). Courier has charged 8 KG."
              className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" />
            <label data-testid="dispute-evidence-drop" className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-4 py-5 text-xs font-semibold text-slate-500 hover:border-shipany-blue hover:text-shipany-blue">
              <UploadCloud className="h-4 w-4" />
              {files.length ? `${files.length} file(s) selected` : "Attach photos / evidence (JPG, PNG, WEBP, PDF — max 5 files, 5MB each)"}
              <input data-testid="dispute-evidence-input" type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))} />
            </label>
            {files.length > 0 && <p className="mt-2 text-[11px] text-slate-400">{files.map((f) => f.name).join(", ")}</p>}
            <button onClick={raise} disabled={busy} data-testid="dispute-submit-btn" className="mt-4 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
              {busy ? "Submitting…" : "Submit Dispute"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
